import { IAccount, PlanStatus } from "~/types/account";
import { ILimit, IPlan, Limits, SubscriptionType } from "~/types/billing";
import { getTotalPlanCost } from "./numbers";

// Payments stay on unless VITE_DISABLE_PAYMENTS is "true". The deploy workflows don't set it.
export const DISABLE_PAYMENTS =
  import.meta.env.VITE_DISABLE_PAYMENTS === "true";
export const DISABLE_TRIAL_FEES = true;

// The trial checking out on `plan` would give, by the API's own rule: none once the
// account (on Shopify, also its shop) has had a trial. Through Stripe a live
// subscription is changed rather than replaced, so it gets no new trial, and Stripe
// gives trials on monthly plans only. Undefined while the plan's trial length is
// unknown.
export const getOfferedTrialDays = (
  account: IAccount | null | undefined,
  plan: IPlan | undefined
): number | undefined => {
  // has_subscribed_before only stands in until the API sends trial_eligible.
  const isEligible = account?.trial_eligible ?? !account?.has_subscribed_before;
  if (!isEligible) return 0;

  const isStripe = account?.payment_provider !== "shopify";
  if (isStripe && account?.active_subscription) return 0;

  if (plan?.trial_days == null) return undefined;
  if (isStripe && plan.interval === SubscriptionType.ANNUAL) return 0;
  return plan.trial_days;
};

// The "Get up to N months off" badge: the best deal among the yearly plans on sale,
// so every screen shows the same number. 0 when no yearly plan offers months off.
export const getMaxMonthsOff = (plans: IPlan[] | undefined): number =>
  (plans ?? [])
    .filter(
      (plan) =>
        plan.status === PlanStatus.ACTIVE &&
        plan.interval === SubscriptionType.ANNUAL
    )
    .reduce((max, plan) => Math.max(max, plan.months_off ?? 0), 0);

// The same tier with or without the $19 add-on (same name, billing period and price)
// is one plan to the user: checkout can subscribe to either version, while the plan
// cards list only one of them.
export const isSamePlan = (
  a: IPlan | null | undefined,
  b: IPlan | null | undefined
): boolean => {
  if (!a || !b) return false;
  if (a.id != null && String(a.id) === String(b.id)) return true;
  return (
    a.name === b.name &&
    a.interval === b.interval &&
    a.cost_per_month === b.cost_per_month
  );
};

export const getCheckoutAction = (
  account: IAccount | null,
  plan: IPlan | undefined
): "trial" | "downgrade" | "upgrade" | "firstSubscription" => {
  const isDowngrade =
    account?.active_subscription?.plan &&
    getTotalPlanCost(plan) < getTotalPlanCost(account.active_subscription.plan);

  // The same trial rule as the checkout wording, so the button never disagrees.
  if (getOfferedTrialDays(account, plan)) return "trial";

  if (!account?.has_subscribed_before) return "firstSubscription";

  if (isDowngrade) {
    return "downgrade";
  }

  return "upgrade";
};

export const isFeatureEnabledOnPlan = (
  limitType: Limits,
  plan: IPlan | undefined
) => {
  if (DISABLE_PAYMENTS) return true;

  const limits: ILimit | Record<any, any> = plan?.limits || {};

  if (limitType === Limits.PAID_PLAN) {
    return !!plan;
  }

  if (!limits) return false;

  return !!limits[limitType];
};
